import { logger } from "./globals";

/**
 * All Available Settings
 */
export type SettingTypes =
  | "apiLoggingInConsole"
  | "arsEnabled"
  | "useBinarySyncFollow"
  | "dev"
  | "historical"
  | "insights"
  | "logSyncFollowMarkers"
  | "mockShellSpaceAutocomplete"
  | "monitoredManagedSwitch"
  | "mustangServer"
  | "multisitePeering"
  | "networkQuality"
  | "n9kC9332DGX2BSwitch"
  | "networkQualityBeta"
  | "optInC3"
  | "portConnections"
  | "scale"
  | "smartHandsDebug"
  | "span"
  | "theme"
  | "restHistory"
  | "agGridTypedTable"
  | "multicellUI"
  | "softwareUpgrade2"
  | "multisiteReachability"
  | "meridian"
  | "enableBindableDeviceModelCheck"
  | "showUnspecifiedDeviceModels"
  | "c880qaServer";

type NiceFormat = {
  setting: SettingTypes;
  active: string | boolean;
  defaultValue: string | boolean | undefined;
  usesDefaultValue: string | boolean | undefined;
};
/**
 * SettingPreferences are stored in a
 * Record as boolean or strings
 */
type SettingPreferences = Record<SettingTypes, string | boolean>;

export default class LocalSettings {
  kvStore: SettingPreferences;
  defaultSettings: SettingPreferences;
  isDefaultMap: SettingPreferences;

  constructor() {
    // build settings map with these default values
    this.kvStore = {
      apiLoggingInConsole: false,
      arsEnabled: false,
      useBinarySyncFollow: true,
      dev: false,
      historical: false, // note this will only be visible if both insights and historical are enabled
      insights: true, // this will be removed once the feature goes in production
      logSyncFollowMarkers: false,
      mockShellSpaceAutocomplete: false,
      monitoredManagedSwitch: false,
      mustangServer: false,
      multisitePeering: false,
      networkQuality: false,
      n9kC9332DGX2BSwitch: false,
      networkQualityBeta: false,
      optInC3: true,
      portConnections: false,
      scale: false,
      smartHandsDebug: false,
      span: false,
      theme: "light-classic",
      restHistory: false,
      agGridTypedTable: false,
      multicellUI: true,
      multisiteReachability: false,
      meridian: false,
      enableBindableDeviceModelCheck: true,
      showUnspecifiedDeviceModels: false,
      softwareUpgrade2: true,
      c880qaServer: false
    };

    // make a copy to track default settings before anything gets modified
    this.defaultSettings = { ...this.kvStore };

    // keep track of which values are overwrites. The values need to be set correctly by loadValues()
    this.isDefaultMap = { ...this.kvStore };

    // load values into map
    this.loadValues();
  }

  loadValues = () => {
    const keys = Object.keys(this.kvStore) as SettingTypes[];
    keys.forEach((k: SettingTypes) => {
      const setting = localStorage.getItem(k);
      if (setting) {
        if (setting === "True") {
          this.kvStore[k] = true;
          this.isDefaultMap[k] = false;
        } else if (setting === "False") {
          this.kvStore[k] = false;
          this.isDefaultMap[k] = false;
        } else {
          this.kvStore[k] = setting;
          this.isDefaultMap[k] = false;
        }
      } else {
        this.isDefaultMap[k] = true;
      }
    });
  };

  validKey = (k: SettingTypes) => {
    if (!Object.hasOwn(this.kvStore, k)) {
      logger.error(
        "Invalid setting '" + k + "'. Available settings:",
        Object.keys(this.kvStore).join(", ")
      );
      return false;
    }
    return true;
  };

  set = (k: SettingTypes, v: boolean | string) => {
    if (this.validKey(k)) {
      this.kvStore[k] = v;
      localStorage.setItem(k, v === true ? "True" : v === false ? "False" : v);
      this.isDefaultMap[k] = false;
    }
  };

  reset = (k: SettingTypes) => {
    if (this.validKey(k)) {
      localStorage.removeItem(k);
      const defaultValue = this.defaultSettings[k] ?? false;
      this.kvStore[k] = defaultValue;
      this.isDefaultMap[k] = true;
    }
  };

  resetAll = () => {
    const keys = Object.keys(this.kvStore) as SettingTypes[];
    keys.forEach((k: SettingTypes) => {
      this.reset(k);
    });
  };

  asObject = () => {
    return this.kvStore;
  };

  get = (k: SettingTypes) => {
    if (this.validKey(k)) {
      return !!this.kvStore[k];
    }
    return false;
  };

  getString = (k: SettingTypes): string => {
    if (this.validKey(k)) {
      return (this.kvStore[k] as string) || "";
    }
    return "";
  };

  getDefault = (k: SettingTypes) => {
    if (this.validKey(k)) {
      return this.defaultSettings[k];
    }
    return false;
  };

  isDefault = (k: SettingTypes) => {
    if (this.validKey(k)) {
      return !!this.isDefaultMap[k];
    }
    return false;
  };

  toggle = (k: SettingTypes) => {
    // only error once
    if (this.validKey(k)) {
      this.set(k, !this.get(k));
    }
  };

  print = () => {
    const niceFormat: NiceFormat[] = [];
    const keys = Object.keys(this.kvStore) as SettingTypes[];
    keys.forEach((k: SettingTypes) => {
      niceFormat.push({
        setting: k,
        active: this.kvStore[k],
        defaultValue: this.defaultSettings[k],
        usesDefaultValue: this.isDefaultMap[k]
      });
    });
    logger.info(niceFormat);
  };
}
